/**
 * SYND-02 — pure PostgREST query builder for `syndicated_public`.
 *
 * Its own module, and free of any import that touches `import.meta.env`, for
 * the same reason lib/api/searchListingsQuery.ts is: the test runner here is
 * plain `npx tsx`, and lib/api/client.ts evaluates Vite env at import time and
 * dies under Node.
 *
 * WHAT A SYNDICATED ROW CAN AND CANNOT ANSWER
 *   The index holds what the seller announced and nothing else: type, price
 *   range, city, neighborhood, bedroom range, an area in the geo hierarchy, and
 *   the amenity labels exactly as the seller published them. It has no
 *   bathrooms column (SYND-13 not done).
 *
 *   A filter the index cannot answer does not return "maybe". It returns NULL
 *   from this builder, meaning "do not run this query at all", and the
 *   syndicated block steps aside while the marketplace results still answer
 *   the filter. That is now exactly two cases (`syndicatedCanAnswer`):
 *     - any bathroom count;
 *     - an amenity UI key with no entry in AMENITY_COLUMN_MAP. An unanswerable
 *       filter suppresses; it never silently widens.
 *
 *   AMENITIES AND MUST-HAVES ARE MENTION-BASED (SYND-19, PO decisions R6/D3,
 *   2026-10-13). Until then any amenity filter suppressed the whole indexed
 *   half: 246 of 1,233 searches in 30 days used one, and 52.8 % of those
 *   returned nothing (median 0) against 24.0 % without one. The view now
 *   derives `amenity_keys` at read time from a curated, STRICT map of exact
 *   normalised labels (`amenity_canonical_map`, migration 20261013200000), and
 *   a filter becomes `amenity_keys=cs.{…}`: an indexed row matches only if the
 *   seller's own announcement mentions EVERY selected feature. Unknown means
 *   excluded, and Home says so in a sentence. Coverage on 2026-10-13: 1,363 of
 *   3,251 live rows carry labels, and the map turns them into security 728 ·
 *   borehole 532 · balcony 393 · pool 247 · gym 167 · garden 129 · title_ready
 *   37 · fireplace 22 · air_con 16 · garage 10 · near_tarmac 10 · furnished 6.
 *   Nothing here writes `amenities` (SYND-06: facts only).
 *
 *   LOCATION IS HIERARCHICAL (SYND-22). Every location tag still matches by
 *   ILIKE on city / neighborhood / title, and also by
 *   `area_path.ov.{"<tag>"}` — the lowercased names and aliases of the row's
 *   area and every ancestor. A search for a county or a town therefore reaches
 *   the neighbourhoods inside it ("Machakos": 26 → ~220 indexed rows). A
 *   union inside the same OR: a row that matched before still matches.
 *
 * NULL PRICES ARE EXCLUDED BY PRICE FILTERS, deliberately. An announcement with
 * no price cannot be claimed to be under KES 10M. It disappears from a
 * price-filtered search and comes back when the filter clears.
 *
 * SYND-11 — TWO PRICES, ONE COLUMN EACH, AND THEY DO NOT MEET
 *   `price_from_kes` is COALESCE(what the seller published in KES, what WE
 *   converted). It drives the filters and the sort below and it is the reason a
 *   listing priced in USD still appears under "up to KES 30M" — which is worth
 *   having and is not in question here.
 *
 *   It is also, on those rows, our arithmetic. Rendering it puts a figure
 *   nobody announced in the position a buyer reads as the price. That is the
 *   pattern we refused to index 62 Knight Frank rows over, and it was live on
 *   14 of our own until this item. So the split below is structural, not a
 *   convention: the FILTER half of this module reads `price_from_kes`, the
 *   DISPLAY half takes `SyndicatedPriceFacts`, and that interface does not name
 *   the column. Neither half can drift into the other's job by accident.
 */

import {
  convertToKes,
  formatKesCompact,
  isRateFresh,
  USD_APPROX_QUALIFIER,
} from '../currency';
import { AMENITY_COLUMN_MAP, pgArrayLiteral } from './searchListingsQuery';

/** Ordering key. `indexed_at` is the view's alias for when we first indexed the
 *  announcement — see the grant note in the SYND-02 migration. */
const ORDER = 'order=indexed_at.desc';

/**
 * FEED-09 — the same ordering, made TOTAL, for anything that pages.
 *
 * `indexed_at` has ties (a batch ingest stamps a whole source in one moment;
 * production holds a 29-row tie today) and inside a tie PostgREST may deal rows
 * differently on different requests, so an offset boundary can land one row on
 * both sides or on neither. `id` is a uuid — the tiebreak is arbitrary, but
 * pagination needs stable, not meaningful. Same string, same reason, as
 * `buildSyndicatedFeedPageQuery` below.
 */
const PAGED_ORDER = 'order=indexed_at.desc,id.desc';

/**
 * The subset of the search UI's filters a syndicated row can be judged against.
 * Deliberately structural rather than `Pick<SearchListingsFilters, …>`: the two
 * surfaces answer different questions, and the day someone adds a column to
 * `listings` should not silently change what this builder claims to support.
 */
export interface SyndicatedFilters {
  locations?: string[];
  minPrice?: number;
  maxPrice?: number;
  /** UI label, e.g. 'Villa' or 'New Development'. Normalised here. */
  propertyType?: string;
  /**
   * HOME-01 fix round 1 — `property_type` values in the view's own spelling
   * (`['villa','bungalow']`), taking precedence over `propertyType`.
   *
   * The index and `listings` carry the SAME six-value vocabulary (verified on
   * prod, 2026-08-14), which is what makes one chip able to filter both sides
   * of the interleave with one array. See src/components/home/propertyTypes.ts.
   */
  propertyTypes?: string[];
  /** Specific bedroom counts; 5 means "5+". */
  beds?: number[];
  /** Present only so the builder can bow out. Never translated (SYND-13). */
  baths?: number[];
  /**
   * SYND-19 — UI amenity / Must-Have keys (`pool`, `titleReady`, …), the same
   * array the marketplace half gets. Translated through AMENITY_COLUMN_MAP into
   * canonical keys and matched with `amenity_keys=cs.{…}`. A key with no map
   * entry makes the whole filter set unanswerable (see syndicatedCanAnswer).
   */
  amenities?: string[];
}

/** Every column of `public.syndicated_public`, as the anon SELECT exposes it. */
export interface SyndicatedListing {
  id: string;
  title: string;
  property_type: string | null;
  /**
   * SYND-11 — `COALESCE(price_from_kes_published, our conversion)`. FILTERING
   * AND SORTING ONLY. It is the column that keeps a foreign-currency listing
   * discoverable in a KES price filter, and it must never reach a buyer's
   * screen: on the 14 rows priced in USD it is OUR arithmetic wearing the same
   * type, weight and colour as a figure the seller published.
   *
   * That is the Knight Frank pattern — a live FX conversion styled as a
   * published price — and it is why we refused to index 62 of their rows. The
   * display rules below therefore cannot see this column: they take
   * `SyndicatedPriceFacts`, which does not name it.
   */
  price_from_kes: number | null;
  /** The announced ceiling, in KES. The source's own figure, never converted. */
  price_to_kes: number | null;
  city: string;
  neighborhood: string | null;
  lat: number | null;
  lng: number | null;
  beds_min: number | null;
  beds_max: number | null;
  source_url: string;
  listed_by: string;
  source_slug: string;
  source_website: string;
  source_logo_url: string | null;
  source_kind: string;
  photo_url: string | null;
  photo_credit: string | null;
  indexed_at: string;
  /**
   * SYND-06 — OUR OWN factual sentence about this listing, authored by hand in
   * SQL. Never the seller's description, and never generated: there is no code
   * path in this repo that writes it and none may be added. See the migration
   * 20260815100000_synd06_summary.sql. NULL is ordinary, not a gap.
   */
  summary: string | null;
  /**
   * SYND-06 — the amenity labels exactly as the seller published them. Display
   * only; search reads `amenity_keys` below, never these strings.
   */
  amenities: string[] | null;
  /**
   * SYND-11 — the source's OWN KES figure, straight from the base table and
   * NULL on every foreign-currency row. This is the display source for a KES
   * price; `price_from_kes` above is not.
   */
  price_from_kes_published: number | null;
  /** SYND-11 — true when `price_from_kes` is our arithmetic, not their figure. */
  price_is_fx_converted: boolean | null;
  /**
   * SYND-11 — the published fact on a foreign-currency row: `210000` / `'USD'`.
   *
   * `numeric` over the wire, which PostgREST has been known to serialise as a
   * string. Typed as both and coerced at the one place that reads it, the same
   * way lib/api/exchangeRateApi.ts handles `exchange_rates.rate`.
   */
  price_original_amount: number | string | null;
  price_original_currency: string | null;
  /** SYND-11 — KES per one unit of `price_original_currency`. `numeric`. */
  fx_rate: number | string | null;
  /** SYND-11 — when that rate was struck. Older than 48h drops the approximation. */
  fx_rate_fetched_at: string | null;
  /**
   * SYND-19 — the canonical amenity keys the seller's labels map to (strict,
   * exact-label map in the database), sorted. `{}` when none map. Search reads
   * this; nothing renders it. Optional because a narrow select omits it.
   */
  amenity_keys?: string[] | null;
  /**
   * SYND-22 — lowercased names and aliases of the row's area and every
   * ancestor (neighbourhood → town → county). Search reads this; nothing
   * renders it. Optional because a narrow select omits it.
   */
  area_path?: string[] | null;
}

/**
 * SYND-19 — the canonical amenity keys for a set of UI keys, or null when any
 * UI key has no AMENITY_COLUMN_MAP entry (an unanswerable filter).
 *
 * `titleReady` → `title_ready`, `nearTarmac` → `near_tarmac`, `airCon` →
 * `air_con`: the canonical keys ARE the `listings` boolean column names, so
 * both halves of a search translate a key the same way. Deduplicated and
 * sorted, so one filter set always produces one query string.
 */
export function syndicatedAmenityKeys(amenities: readonly string[] | undefined): string[] | null {
  if (!amenities || amenities.length === 0) return [];
  const keys = new Set<string>();
  for (const key of amenities) {
    const column = Object.prototype.hasOwnProperty.call(AMENITY_COLUMN_MAP, key)
      ? AMENITY_COLUMN_MAP[key]
      : undefined;
    if (!column) return null;
    keys.add(column);
  }
  return [...keys].sort();
}

/**
 * False when the filter set asks something the index cannot answer, so no
 * syndicated query should be issued at all.
 *
 * Exactly two cases since SYND-19: any bathroom count (the view has no
 * bathrooms column), or an amenity UI key with no AMENITY_COLUMN_MAP entry. An
 * unanswerable filter suppresses the indexed half; it never silently widens
 * it. Amenity and Must-Have keys the map knows are ANSWERED, by
 * `amenity_keys=cs.{…}`.
 *
 * Split out from buildSyndicatedQuery rather than left inline so the rule has
 * one statement and can be asserted directly. Home asks it to decide which
 * sentence to print above the results and whether to offer another page of
 * syndicated rows before either request exists. Any surface reasoning about
 * the block BEFORE fetching (a heading, a count, a pager, a skeleton) must ask
 * this rather than re-derive it, which is how the two answers drift apart.
 */
export function syndicatedCanAnswer(filters: SyndicatedFilters): boolean {
  if (filters.baths && filters.baths.length > 0) return false;
  if (syndicatedAmenityKeys(filters.amenities) === null) return false;
  return true;
}

/**
 * Bedroom matching over the announced range, mirroring the shape
 * buildSearchListingsQuery uses for `listings` (BUG-020).
 *
 *   single-config (beds_max IS NULL) → beds_min == v
 *   range (beds_max IS NOT NULL)     → beds_min <= v <= beds_max
 *   v >= 5 ("5+")                    → the upper bound, or beds_min when there
 *                                      is none, reaches 5
 *
 * A development announced as "1–3 bed" therefore matches a "2 bed" filter, and
 * a flat 1-bed does not.
 */
function bedsRangeExpr(value: number): string {
  if (value >= 5) {
    return `or(beds_max.gte.${value},and(beds_min.gte.${value},beds_max.is.null))`;
  }
  return `or(and(beds_min.eq.${value},beds_max.is.null),and(beds_min.lte.${value},beds_max.gte.${value}))`;
}

/**
 * The announced range overlaps the buyer's range.
 *
 *   maxPrice → the bottom of the announced range is at or under it.
 *   minPrice → the top of the announced range reaches it; when there is no
 *              upper bound ("from KES 9M"), the bottom stands in for it.
 */
function priceExprs(filters: SyndicatedFilters): { top: string[]; complex: string[] } {
  const top: string[] = [];
  const complex: string[] = [];

  if (filters.maxPrice) top.push(`price_from_kes=lte.${filters.maxPrice}`);
  if (filters.minPrice) {
    complex.push(
      `or(price_to_kes.gte.${filters.minPrice},and(price_to_kes.is.null,price_from_kes.gte.${filters.minPrice}))`
    );
  }

  return { top, complex };
}

/**
 * Build the query string, or null when the filters cannot be answered.
 *
 * `limit` caps how many syndicated rows the request returns.
 *
 * FEED-09 — THE DEFAULT IS NO LONGER A SEARCH'S ANSWER. Twelve was SYND-02's
 * product number for a block of extras at the bottom of a page, and SYND-06
 * kept it when those rows moved INTO the result list. On a Nairobi search that
 * meant twelve of 1,030 matching entries were reachable and the eleventh
 * hundred did not exist as far as the buyer was concerned. Search now pages
 * through the whole filtered set via `buildSyndicatedSearchPageQuery` below,
 * which passes an explicit page size on every request.
 *
 * The default stays twelve for callers that genuinely want a fixed dozen — a
 * carousel, a teaser row, anything whose size is a layout decision rather than
 * a page-size calculation. It is a ceiling someone chose, and no result list
 * may fall through to it.
 */
/**
 * The `property_type` clause for the index, plural-first.
 *
 * Mirrors `propertyTypeClause` in searchListingsQuery.ts deliberately: the two
 * halves of the interleave have to answer the same question about type, and
 * the surest way to make them disagree is to let each one write its own rule.
 * Both read the same chip array; both emit `in.(…)` for it.
 */
function syndicatedPropertyTypeClause(filters: SyndicatedFilters): string | null {
  if (filters.propertyTypes && filters.propertyTypes.length > 0) {
    return `property_type=in.(${filters.propertyTypes.join(',')})`;
  }
  if (filters.propertyType) {
    // 'New Development' → 'new_development'. The UI labels and the view's CHECK
    // list agree once cased and underscored; see types.ts PropertyType.
    return `property_type=eq.${filters.propertyType.toLowerCase().replace(/\s+/g, '_')}`;
  }
  return null;
}

export function buildSyndicatedQuery(
  filters: SyndicatedFilters,
  limit: number = 12
): string | null {
  if (!syndicatedCanAnswer(filters)) return null;

  const top: string[] = [];
  // Clauses containing a logical OR. PostgREST collapses duplicate top-level
  // `or=` keys — only one survives — so more than one has to be combined under
  // a single `and=(...)`. Same constraint, same fix, as searchListingsQuery.ts.
  const complex: string[] = [];

  if (filters.locations && filters.locations.length > 0) {
    // No `address` column here: the index holds a city and a neighborhood, not
    // a street. `title` is included because a development's name is often the
    // only place the area appears ("Riverside Square").
    //
    // SYND-22: plus `area_path.ov.{"<tag>"}` — the row's area or any ancestor
    // is named by the tag (lowercased, as geo_area_path stores names). One
    // more alternative inside the same OR, so nothing that matched before
    // stops matching. A tag that cannot be written as an array literal (it
    // contains a brace) keeps its ILIKE alternatives and gets no area_path one.
    const conds = filters.locations.flatMap((loc) => {
      const esc = loc.replace(/'/g, "''");
      const alternatives = [`city.ilike.*${esc}*`, `neighborhood.ilike.*${esc}*`, `title.ilike.*${esc}*`];
      const area = pgArrayLiteral([loc.trim().toLowerCase()]);
      if (area !== '{}') alternatives.push(`area_path.ov.${area}`);
      return alternatives;
    });
    complex.push(`or(${conds.join(',')})`);
  }

  if (filters.beds && filters.beds.length > 0) {
    const exprs = filters.beds.map(bedsRangeExpr);
    complex.push(exprs.length === 1 ? exprs[0] : `or(${exprs.join(',')})`);
  }

  const price = priceExprs(filters);
  top.push(...price.top);
  complex.push(...price.complex);

  const typeClause = syndicatedPropertyTypeClause(filters);
  if (typeClause) top.push(typeClause);

  // SYND-19: every selected amenity / Must-Have, as canonical keys. Top-level
  // (no OR inside), so it never competes with the single `or=`/`and=` slot.
  const amenityKeys = syndicatedAmenityKeys(filters.amenities) ?? [];
  if (amenityKeys.length > 0) top.push(`amenity_keys=cs.{${amenityKeys.join(',')}}`);

  if (complex.length === 1) {
    const c = complex[0];
    if (c.startsWith('or(')) top.push(`or=${c.slice(2)}`);
    else if (c.startsWith('and(')) top.push(`and=${c.slice(3)}`);
    else top.push(c);
  } else if (complex.length > 1) {
    top.push(`and=(${complex.join(',')})`);
  }

  top.push('select=*', ORDER, `limit=${limit}`);
  return top.join('&');
}

/**
 * FEED-09 — one page of a SEARCH's syndicated half.
 *
 * The predicate is `buildSyndicatedQuery`'s, reused rather than rebuilt: what a
 * syndicated filter MEANS has one definition in this file, and a second copy of
 * the location/price/beds clauses is how the rows and the count that sits above
 * them would start answering different questions. Only the tail differs, and
 * every difference is load-bearing:
 *
 *   the ordering  is made total with the `id` tiebreak (see PAGED_ORDER). The
 *                 single-fetch query does not need one — a request cannot
 *                 disagree with itself — and pagination does.
 *   the limit     is the caller's page size, always. There is no default to
 *                 fall through to here, which is the point of the function.
 *   the offset    is where the page starts.
 *
 * Returns null in exactly the case `buildSyndicatedQuery` does — a filter set
 * the index cannot answer — and the caller must read that null as "this search
 * has no syndicated half at all", the same conclusion
 * `buildSyndicatedCountQuery` reaches for those filters. That agreement is what
 * keeps a suppressed search's list and its counter telling the same story.
 */
export function buildSyndicatedSearchPageQuery(
  filters: SyndicatedFilters,
  offset: number,
  limit: number
): string | null {
  const base = buildSyndicatedQuery(filters, limit);
  if (base === null) return null;
  return `${base.replace(ORDER, PAGED_ORDER)}&offset=${offset}`;
}

/**
 * HOME-01 fix round 1 — the same filtered question, asked as a count.
 *
 * Paired with `Prefer: count=exact`, so the total arrives in `Content-Range`
 * and no rows are downloaded to arrive at it. Returns null in exactly the case
 * `buildSyndicatedQuery` does — a filter set the index cannot answer — and the
 * caller must read that null as "this search has no syndicated half", i.e. a
 * contribution of ZERO to the page total, not as an unknown. That is the same
 * conclusion `buildSyndicatedSearchPageQuery` reaches when it returns null for
 * those filters, which is what keeps the counter and the list agreeing.
 *
 * Deliberately uncapped: this is how many rows MATCH. Until FEED-09 that was a
 * different question from how many the search would show — a search showed
 * twelve — and the gap between the two numbers was the bug. The search pages
 * through the whole filtered set now, so the two questions have converged, and
 * this stays uncapped for the reason it always should have been: a total that
 * moves with a page size is not a total.
 */
export function buildSyndicatedCountQuery(filters: SyndicatedFilters): string | null {
  const full = buildSyndicatedQuery(filters, 1);
  if (full === null) return null;
  // Strip the row-shaped tail (`select=*`, ordering, limit) and re-ask for a
  // header. Splitting on the known tail rather than rebuilding the predicate
  // keeps ONE definition of what a syndicated filter means.
  const predicate = full
    .split('&')
    .filter(
      (part) =>
        !part.startsWith('select=') && !part.startsWith('order=') && !part.startsWith('limit=')
    );
  return [...predicate, 'select=id', 'limit=1'].join('&');
}

/**
 * One entry by id, for the detail page.
 *
 * The view already restricts itself to live rows from sources that have not
 * opted out, so a removed listing is simply not found — the page renders its
 * not-found state and never has to reason about status.
 */
export function buildSyndicatedByIdQuery(id: string): string {
  return `id=eq.${encodeURIComponent(id)}&select=*&limit=1`;
}

/**
 * SYND-15 — a set of entries by id, for the Saved page.
 *
 * The Saved page holds a list of syndicated uuids and no other fact about them.
 * `saved_properties` is not joinable to the view from the client (PostgREST
 * embeds require a foreign key it cannot see through `syndicated_public`, which
 * is security_invoker over the base table), so hydration is a second request
 * with an `in.(…)` predicate — the ONE route to syndicated display data from
 * the client, exactly as the feed and the detail page use it.
 *
 * IDS THAT ARE NOT IN THE VIEW SIMPLY DO NOT COME BACK, and that is the whole
 * error handling: a saved entry whose source has since opted out, or whose row
 * was removed, is absent from the response and the page renders nothing for it.
 * No ghost card, no "unavailable" placeholder, no error — the same answer the
 * detail page gives for the same three causes (see getSyndicatedListing).
 *
 * `null` for an EMPTY id list rather than a query that would return the whole
 * view: `id=in.()` is not valid PostgREST, and a caller with nothing to ask
 * about must make no request at all.
 *
 * The order is the view's own, newest-indexed first, and the caller re-orders
 * by when the buyer saved each one — that fact lives in `saved_properties` and
 * cannot be asked of this view.
 */
export function buildSyndicatedByIdsQuery(ids: string[]): string | null {
  const unique = Array.from(new Set(ids.filter(id => typeof id === 'string' && id.length > 0)));
  if (unique.length === 0) return null;
  // Quoted per-value: a uuid needs no quoting, but an `in.(…)` list built by
  // raw concatenation is one malformed id away from a syntax error that reads
  // as an empty Saved page.
  const list = unique.map(id => `"${encodeURIComponent(id)}"`).join(',');
  return `id=in.(${list})&select=*&${ORDER}&limit=${unique.length}`;
}

/**
 * SYND-10 — one page of the unfiltered Home feed's syndicated half.
 *
 * A different query from buildSyndicatedQuery on purpose, not an oversight.
 * The feed pages through the WHOLE view under one chip; the search pages
 * through a filtered slice of it. Both need the total ordering PAGED_ORDER
 * gives them — see its note for why offset pagination cannot survive a tie —
 * and FEED-09 made the search path a pager too, which is why the tiebreak now
 * has one definition instead of being spelled out here alone.
 *
 * ONE filter, and only one: the home page's property-type chip.
 *
 * HOME-01 fix round 1. Every other filter still belongs to the search path —
 * a location, a price ceiling or a bed count opens a search, and the search
 * path owns the syndicated half there. A type chip is not a search; it is a
 * narrowing of the same browse, and routing it through the search path was
 * what excluded the index from it: the search path capped the syndicated half
 * at twelve and hid pagination, so "Apartment" would have shown a dozen of the
 * 843 indexed apartments and then stopped, under a counter claiming hundreds.
 * Filtering the PAGER instead leaves SYND-10's interleave, batch sizes, dedupe
 * and termination rule exactly as they are — the only thing that changes is
 * which rows the view hands back. (FEED-09 has since given the search path a
 * pager of its own, so that cap is gone; the split stands on what it always
 * stood on — a chip narrows a browse and a location opens a search.)
 *
 * `in.(…)` because a chip can name more than one value; absent or empty means
 * no type filter, and the query string is then identical to the one this
 * builder has always produced.
 *
 * `select` (SYND-21): the server render asks for the feed's first page with
 * only the card columns (SYNDICATED_CARD_SELECT), because those rows are
 * serialised into the HTML. Same filter, same order, same limit and offset as
 * the SPA's first page — the projection is the only thing that differs, which
 * is what lets both trees show the same first 30 cards.
 */
export function buildSyndicatedFeedPageQuery(
  offset: number,
  limit: number,
  propertyTypes?: readonly string[],
  select: string = '*'
): string {
  return `${propertyTypeClause(propertyTypes)}select=${select}&order=indexed_at.desc,id.desc&limit=${limit}&offset=${offset}`;
}

/**
 * The type chip as a leading query-string clause: `property_type=in.(a,b)&`,
 * or '' for All types. One spelling for every builder a chip narrows, so the
 * feed pager and "New this week" can never filter the same chip two ways.
 */
function propertyTypeClause(propertyTypes?: readonly string[]): string {
  return propertyTypes && propertyTypes.length > 0
    ? `property_type=in.(${propertyTypes.join(',')})&`
    : '';
}

// =============================================================================
// SYND-18 — "New this week": the week's indexed additions
// =============================================================================

/**
 * The view behind the indexed half of "New this week" — migration
 * 20261013100000_synd18_syndicated_new_this_week.sql. It is syndicated_public
 * narrowed to rows indexed in the last 7 × 24 h, at most two per
 * (source, property type), so every rule about which indexed rows may be shown
 * at all is inherited from syndicated_public rather than restated.
 */
export const NEW_THIS_WEEK_VIEW = 'syndicated_new_this_week';

/**
 * How many indexed candidates both trees ask for (Brain ruling 3, 2026-10-13).
 *
 * The row shows at most three indexed cards beside five marketplace ones, and
 * up to eight when there are none. The view caps each source at two per TYPE,
 * so one source can supply up to twelve of the 24 rows (six types × 2), and the
 * client then keeps two of them. Twenty-four is usually enough to fill the row,
 * but it is not a guarantee: if the newest 24 rows come from only two or three
 * sources that each span several types, the indexed block stops at four to six
 * cards even when older sources have eligible rows. Known and accepted with the
 * ruled limit; a per-source rank on the unfiltered request would remove it.
 */
export const NEW_THIS_WEEK_FETCH_LIMIT = 24;

/**
 * Exactly the columns an indexed card reads: `listingViewFromSyndicated`, the
 * `syndicatedPriceLine` it calls (the SYND-11 price facts), and `source_slug`
 * for the per-source cap. The server-rendered page selects these and nothing
 * else, because every row it fetches is serialised into the page for the
 * first client paint.
 *
 * Deliberately NOT here: `price_from_kes`. On a foreign-currency row it holds
 * our conversion, and SYND-11 keeps it out of every display path.
 */
export const SYNDICATED_CARD_COLUMNS = [
  'id',
  'title',
  'property_type',
  'city',
  'neighborhood',
  'beds_min',
  'beds_max',
  'listed_by',
  'source_slug',
  'photo_url',
  'photo_credit',
  'indexed_at',
  'amenities',
  'price_from_kes_published',
  'price_to_kes',
  'price_original_amount',
  'price_original_currency',
  'price_is_fx_converted',
  'fx_rate',
  'fx_rate_fetched_at',
] as const;

/** An indexed row as a card needs it. A full `SyndicatedListing` satisfies it. */
export type SyndicatedCardRow = Pick<SyndicatedListing, (typeof SYNDICATED_CARD_COLUMNS)[number]>;

/** `SYNDICATED_CARD_COLUMNS` as a PostgREST `select=` value. */
export const SYNDICATED_CARD_SELECT = SYNDICATED_CARD_COLUMNS.join(',');

/**
 * SYND-18 — the query string for `syndicated_new_this_week`.
 *
 * Newest first by the total order the feed pager uses (`PAGED_ORDER`), so a tie
 * on `indexed_at` — a batch ingest stamps a whole source in one moment — is
 * broken by `id` the same way on every request, and the server render and the
 * hydrated SPA pick the same rows.
 *
 * `propertyTypes` is the SPA's active type chip, sent server-side in the same
 * clause shape as `buildSyndicatedFeedPageQuery`. The view caps per
 * (source, type), so filtering after the cap still finds each source's newest
 * two of the type asked for. The server render has no chip and sends none.
 *
 * `select` defaults to every column; the server render passes
 * `SYNDICATED_CARD_SELECT` instead.
 */
export function buildSyndicatedNewThisWeekQuery(
  limit: number = NEW_THIS_WEEK_FETCH_LIMIT,
  propertyTypes?: readonly string[],
  select: string = '*'
): string {
  return `${propertyTypeClause(propertyTypes)}select=${select}&${PAGED_ORDER}&limit=${limit}`;
}

/**
 * SYND-18 — GET `syndicated_new_this_week` and answer [] on ANY failure. The
 * one fetch both trees share, so the rule "a lost request costs the indexed
 * cards and nothing else" has one implementation and one test.
 *
 * The promise never rejects: a network error, a non-2xx status, a body that is
 * not JSON and a JSON body that is not an array each resolve to [], with
 * exactly one call to `log`. That is what lets the server render put this in
 * the same Promise.all as the page's other queries, and what keeps the SPA from
 * raising an unhandled rejection (the sales demo's browser test fails on any
 * page error).
 *
 * Pure apart from the call it is handed: no env, no client.ts. `fetchImpl`
 * defaults to the global fetch at call time, so the sales demo's in-page fetch
 * wrapper still answers it; tests pass a stub.
 */
export async function fetchNewThisWeekRows<Row>(
  url: string,
  init: RequestInit,
  log: (message: string, detail: unknown) => void = (message, detail) => console.error(message, detail),
  fetchImpl?: typeof fetch
): Promise<Row[]> {
  return fetchSyndicatedRowsOrEmpty<Row>(
    url,
    init,
    'Failed to load new-this-week indexed rows:',
    log,
    fetchImpl
  );
}

/**
 * The never-rejecting GET behind `fetchNewThisWeekRows`, with the log line
 * named by the caller. SYND-21's server render reads the feed's first indexed
 * page through it (src/pages/index/+data.tsx): the same rule — a lost request
 * costs the indexed cards and nothing else — and a log line that says which
 * request was lost.
 */
export async function fetchSyndicatedRowsOrEmpty<Row>(
  url: string,
  init: RequestInit,
  message: string,
  log: (message: string, detail: unknown) => void = (message, detail) => console.error(message, detail),
  fetchImpl?: typeof fetch
): Promise<Row[]> {
  try {
    const response = await (fetchImpl ?? fetch)(url, init);
    if (!response.ok) {
      log(message, response.status);
      return [];
    }
    const body: unknown = await response.json();
    if (!Array.isArray(body)) {
      log(message, 'response body is not an array');
      return [];
    }
    return body as Row[];
  } catch (error: unknown) {
    log(message, error instanceof Error ? error.message : error);
    return [];
  }
}

/**
 * SYND-10 — the total from a PostgREST `Content-Range` header.
 *
 * `Prefer: count=exact` answers with `0-3/80` — a star instead of the range
 * when the page is empty — and the number after the slash is the whole view's
 * row count, which is what "Showing 24 of 168" needs for its right-hand side.
 * A star after the slash, or a missing header, parses to null: an unknown
 * total is a fact the caller handles (keep the last known one), not a zero.
 */
export function parseContentRangeTotal(header: string | null): number | null {
  if (!header) return null;
  const match = header.match(/\/(\d+)\s*$/);
  return match ? parseInt(match[1], 10) : null;
}

// =============================================================================
// PRESENTATION — pure, shared by the card and the detail page
// =============================================================================
// These live here rather than beside the fetchers in syndicatedApi.ts for the
// same reason the builders do: that module imports ./client, which evaluates
// import.meta.env at import time and dies under plain Node, and these are the
// functions most worth unit-testing. syndicatedApi.ts re-exports them, so
// components still import from one place.

/**
 * SYND-11 — the columns the price rules are allowed to see.
 *
 * `price_from_kes` is deliberately not among them. On a row priced in another
 * currency that column holds OUR conversion, and a display path that can reach
 * it is a display path that can print our arithmetic in the seller's voice.
 * Leaving it out of the parameter type is not documentation, it is the
 * enforcement: nothing here can read a column this interface does not name.
 *
 * Every field is optional so a test — or a caller holding a narrower row than
 * the full view — can state only the case it means, and so that a missing
 * column reads as "not published" rather than as a crash.
 */
export interface SyndicatedPriceFacts {
  /** The source's own KES figure. NULL on a foreign-currency row. */
  price_from_kes_published?: number | null;
  /** The source's own KES ceiling. Never a conversion. */
  price_to_kes?: number | null;
  /** The published amount on a foreign-currency row. `numeric` over the wire. */
  price_original_amount?: number | string | null;
  /** Its ISO code, e.g. 'USD'. Without one the amount has no unit and no meaning. */
  price_original_currency?: string | null;
  price_is_fx_converted?: boolean | null;
  /** KES per one unit of `price_original_currency`. `numeric` over the wire. */
  fx_rate?: number | string | null;
  fx_rate_fetched_at?: string | null;
}

/**
 * SYND-11 — what the seller actually published, before anything is formatted.
 *
 * One classifier, consulted by every price surface, so that "is this a KES row
 * or a foreign-currency row" has a single answer per listing. The three
 * surfaces used to each re-derive it from whichever columns were in scope,
 * which is precisely how a converted figure reached a card.
 */
export type SyndicatedPublishedPrice =
  | { kind: 'kes'; from: number | null; to: number | null }
  | { kind: 'foreign'; code: string; amount: number }
  | { kind: 'none' };

/**
 * `numeric` arrives as a number or a string depending on how PostgREST was
 * built. Same coercion, same reason, as lib/api/exchangeRateApi.ts.
 */
function toFiniteNumber(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
}

/**
 * An ISO-4217-shaped code, upper-cased — or null when there is nothing we can
 * honestly put in front of a number.
 *
 * Deliberately a SHAPE check and not an allow-list. EUR and GBP rows are
 * already queued behind this work, and a list naming today's currencies would
 * meet them by hiding a published price. Anything three letters long gets
 * printed beside its amount, known to us or not — that is the locked rule, and
 * an unrecognised code costs the buyer nothing because the approximation
 * beside it is gated on a rate, not on a code.
 */
function normaliseCurrencyCode(code: string | null | undefined): string | null {
  const trimmed = code?.trim().toUpperCase();
  return trimmed && /^[A-Z]{3}$/.test(trimmed) ? trimmed : null;
}

/**
 * `USD 210,000` — a published foreign amount, formatted.
 *
 * Both the primary figure and the "converted from …" clause inside the
 * approximation come through here, so the sentence quotes the price above it
 * character for character rather than re-deriving something close to it.
 *
 * The locale is pinned, the same decision as CURR-03's `formatKesAmount`: a
 * bare toLocaleString() follows the DEVICE, so a European phone would print
 * "USD 210 000" beside "KES 9,000,000" on the same card.
 */
function formatForeignAmount(published: { code: string; amount: number }): string {
  return `${published.code} ${Math.round(published.amount).toLocaleString('en-US')}`;
}

/** The foreign half, or null. Both halves are required — see the code note. */
function publishedForeignPrice(
  listing: SyndicatedPriceFacts
): { code: string; amount: number } | null {
  const code = normaliseCurrencyCode(listing.price_original_currency);
  const amount = toFiniteNumber(listing.price_original_amount);

  // An amount with no code is a number with no unit, and on this site an
  // unlabelled number reads as KES. That is worse than saying nothing, so a
  // row in that state falls through to "Price on request".
  if (code === null || amount === null || amount <= 0) return null;

  return { code, amount };
}

export function syndicatedPublishedPrice(listing: SyndicatedPriceFacts): SyndicatedPublishedPrice {
  const from = listing.price_from_kes_published ?? null;
  const to = listing.price_to_kes ?? null;

  // 1. The source published in KES. Unchanged by SYND-11, deliberately and
  //    first: 1,373 of the 1,554 indexed rows are this case, and they must
  //    render exactly as they did — including the buyer's USD toggle.
  if (from != null) return { kind: 'kes', from, to };

  // 2. The source published in another currency. THIS is the fact, and it is
  //    what displays. `price_to_kes` is not consulted here even if it is set:
  //    a KES ceiling with a USD floor is not a range anyone announced, and
  //    there is no column for a foreign ceiling to pair it with.
  const foreign = publishedForeignPrice(listing);
  if (foreign) return { kind: 'foreign', ...foreign };

  // 3. A ceiling and no floor — "Up to KES 14,500,000". Unreachable in
  //    production today and preserved anyway, because it is the source's own
  //    figure and dropping it would lose a published price.
  if (to != null) return { kind: 'kes', from: null, to };

  return { kind: 'none' };
}

/**
 * The announced price, or null when nothing was announced.
 *
 * An announcement usually gives a floor and no ceiling, which is why this reads
 * "From KES 9,000,000" rather than inventing a point price. A range prints both
 * ends.
 *
 * SYND-11: on a row the seller priced in another currency this reads
 * "USD 210,000" — the published figure, in the published currency. The code
 * rather than a symbol, because there is no symbol table that can be right for
 * a currency we have not met yet, and because "USD 210,000" cannot be misread
 * as the KES prices it sits beside in a result list. The KES approximation is
 * a separate function returning a separate, marked string; it is never this
 * one's answer.
 *
 * The null is load-bearing and stays null: this function answers "what did the
 * seller announce", and the answer is sometimes nothing. What the UI DOES about
 * that is `syndicatedPriceLine` below, which is a separate question with a
 * separate answer, and keeping them apart is what stops "Price on request" from
 * ever being mistaken for something the seller said.
 */
export function formatSyndicatedPrice(listing: SyndicatedPriceFacts): string | null {
  const published = syndicatedPublishedPrice(listing);
  const kes = (n: number) => `KES ${n.toLocaleString('en-KE')}`;

  if (published.kind === 'foreign') return formatForeignAmount(published);

  if (published.kind === 'kes') {
    const { from, to } = published;
    if (from != null && to != null) {
      return from === to ? kes(from) : `${kes(from)} – ${kes(to)}`;
    }
    if (from != null) return `From ${kes(from)}`;
    if (to != null) return `Up to ${kes(to)}`;
  }

  return null;
}

/**
 * SYND-11 — the KES approximation that may sit BESIDE a published foreign
 * price, or null when it may not.
 *
 * Reads, in full: `≈ KES 27.2M · converted from USD 210,000 at today’s rate`.
 * Every clause is doing work. The `≈` and the compaction say the figure is not
 * exact; "converted from" says which way the arithmetic ran and therefore which
 * of the two figures the seller actually published; and the qualifier — the
 * same string the buyer-side USD line uses, so the two cannot drift into
 * saying different things — says the rate is today's and not held.
 *
 * It is null unless ALL of these hold:
 *   * the row is one we converted (`price_is_fx_converted`), so we are never
 *     re-deriving a figure the seller gave us;
 *   * there is a published foreign price to convert FROM;
 *   * the row carries a usable rate;
 *   * that rate was struck within the freshness window CURR-01 set.
 *
 * A stale or missing rate drops this line and NOTHING else. The published
 * price does not go stale — it is a fact about an announcement, not about a
 * market — so it stays exactly where it was. That asymmetry is the whole rule:
 * we withhold our arithmetic, never the seller's figure.
 */
export function syndicatedKesApprox(
  listing: SyndicatedPriceFacts,
  now: number = Date.now()
): string | null {
  if (listing.price_is_fx_converted !== true) return null;

  const published = syndicatedPublishedPrice(listing);
  if (published.kind !== 'foreign') return null;

  const rate = toFiniteNumber(listing.fx_rate);
  if (rate === null || rate <= 0) return null;

  const fetchedAt = listing.fx_rate_fetched_at;
  if (!fetchedAt || !isRateFresh(fetchedAt, now)) return null;

  const kes = convertToKes(published.amount, rate);
  if (kes === null) return null;

  return (
    `≈ ${formatKesCompact(kes)} · converted from ` +
    `${formatForeignAmount(published)} ${USD_APPROX_QUALIFIER}`
  );
}

/** "Riverside, Nairobi" — or just the city when the announcement gave no area. */
export function formatSyndicatedLocation(listing: {
  city: string;
  neighborhood: string | null;
}): string {
  return listing.neighborhood ? `${listing.neighborhood}, ${listing.city}` : listing.city;
}

/** "1 – 3 bed" / "2 bed" / null when no bedroom count was announced. */
export function formatSyndicatedBeds(listing: {
  beds_min: number | null;
  beds_max: number | null;
}): string | null {
  const { beds_min: min, beds_max: max } = listing;
  if (min == null && max == null) return null;
  if (min != null && max != null && max > min) return `${min} – ${max} bed`;
  return `${min ?? max} bed`;
}

/** What the price position reads when the announcement gave no price. */
export const PRICE_ON_REQUEST = 'Price on request';

/**
 * SYND-06 — what actually goes in the price position, card and detail page.
 *
 * SYND-02 omitted the price line entirely when nothing was announced, on the
 * grounds that "Price on request" claims something about the seller's process.
 * That reasoning was right about the claim and wrong about the outcome: a card
 * with no price row in a grid of cards that have one reads as a broken card,
 * and the buyer's next thought is that something is being withheld. The product
 * decision is that the position is always filled, never with a number we made
 * up — which is the same principle SYND-02 was reaching for, applied to the
 * layout instead of the sentence.
 *
 * `announced` is not a formatting hint the caller may ignore. It says whether
 * this string came from the seller, and the UI renders it in the secondary text
 * colour when it did not, so the two are never read as the same kind of fact.
 *
 * SYND-11 widened the return value from a label to the whole price block,
 * because the two things that may sit under the label are mutually exclusive
 * and deciding which is not a judgement a surface should be making twice:
 *
 *   `toggleKes`  the amounts the buyer's KES/USD toggle may convert. Non-null
 *                ONLY when the seller published in KES. A row published in
 *                another currency hands back null, so no surface can route a
 *                foreign price — or our conversion of one — through <UsdApprox>.
 *   `kesApprox`  the marked KES approximation for a foreign-currency row.
 *
 * See the file's SYND-11 note on the toggle for why a USD-published row shows
 * no USD approximation even when the buyer has selected USD.
 */
export interface SyndicatedPriceLine {
  /** The price position. Always filled — "Price on request" when nothing was announced. */
  label: string;
  /** True when `label` is a figure the seller published. */
  announced: boolean;
  /** Published KES amounts the buyer-side USD toggle may convert, or null. */
  toggleKes: { from: number; to: number | null } | null;
  /** The marked KES approximation beside a published foreign price, or null. */
  kesApprox: string | null;
}

export function syndicatedPriceLine(
  listing: SyndicatedPriceFacts,
  now: number = Date.now()
): SyndicatedPriceLine {
  const announced = formatSyndicatedPrice(listing);
  const published = syndicatedPublishedPrice(listing);

  // The toggle governs how a KES price is PRESENTED to a buyer. It has nothing
  // to convert unless the seller's own figure is in KES, and `from` is what
  // carries it — a ceiling with no floor has no primary figure to sit under.
  const toggleKes =
    published.kind === 'kes' && published.from != null
      ? { from: published.from, to: published.to }
      : null;

  return {
    label: announced ?? PRICE_ON_REQUEST,
    announced: announced !== null,
    toggleKes,
    kesApprox: syndicatedKesApprox(listing, now),
  };
}

/** How much of the summary a card shows before it gives up. */
export const CARD_SUMMARY_MAX = 90;

/**
 * SYND-06 — the summary, cut to fit a card.
 *
 * Cuts on a word boundary, because a summary sliced mid-word looks like a bug
 * rather than a truncation, and returns null for anything that is empty once
 * trimmed so a caller can drop the line rather than render a blank one.
 *
 * The detail page does NOT use this — it prints the whole thing. This is the
 * card's problem alone.
 */
export function truncateSummary(
  summary: string | null | undefined,
  max: number = CARD_SUMMARY_MAX
): string | null {
  const text = summary?.trim();
  if (!text) return null;
  if (text.length <= max) return text;

  const cut = text.slice(0, max);
  // When the character just past the budget is whitespace, the cut already fell
  // between words and there is nothing to walk back — trimming to the previous
  // space here would throw away a word that fitted perfectly.
  const cutFellBetweenWords = /\s/.test(text.charAt(max));
  const lastSpace = cut.lastIndexOf(' ');
  // A "word" longer than the whole budget (a URL, a pasted identifier) has no
  // boundary to cut on, so fall back to the hard slice rather than to nothing.
  const body = cutFellBetweenWords || lastSpace <= 0 ? cut : cut.slice(0, lastSpace);
  return `${body.replace(/[\s,;:.\-–—]+$/, '')}…`;
}

/**
 * SYND-06 — every amenity tag on a listing, cleaned. The detail page's list.
 *
 * The cleaning is not decoration. This column is typed by hand in SQL, so a
 * stray empty string or a trailing space in an array literal is a normal
 * authoring slip, and it would otherwise render as a blank pill.
 */
export function syndicatedAmenityTags(amenities: string[] | null | undefined): string[] {
  if (!amenities) return [];
  return amenities
    .map((a) => (typeof a === 'string' ? a.trim() : ''))
    .filter((a) => a.length > 0);
}

/** How many amenity tags a card shows. */
export const CARD_AMENITY_MAX = 3;

/**
 * SYND-06 — the same tags, cut to what a card can carry.
 *
 * No "+2 more" affordance, unlike the marketplace card. There, the count is a
 * fact about the property: the listing agent filled in every amenity field and
 * four of seven are showing. Here it would be a fact about how much someone has
 * written so far, and "+2 more" on a hand-authored list advertises the gap
 * between the listings we have got to and the ones we have not.
 */
export function cardAmenityTags(amenities: string[] | null | undefined): string[] {
  return syndicatedAmenityTags(amenities).slice(0, CARD_AMENITY_MAX);
}
